import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import { ArrowLeft } from "lucide-react";

const PAGE_SIZE = 5;

function PaginationControls({ currentPage, totalPages, onPageChange }) {
  const getPages = () => {
    if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage === 1) return [1, 2, 3];
    if (currentPage === totalPages) return [totalPages - 2, totalPages - 1, totalPages];
    return [currentPage - 1, currentPage, currentPage + 1];
  };

  const pages = getPages();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 12, paddingTop: 12, borderTop: '1px solid #F2F2F2' }}>
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #E6E5E5', background: '#FFFFFF', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="#1D2338" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {pages[0] > 1 && (
        <>
          <button onClick={() => onPageChange(1)} style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #E6E5E5', background: '#FFFFFF', color: '#1D2338', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>1</button>
          {pages[0] > 2 && <span style={{ fontSize: 13, color: '#9CA3AF' }}>...</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid', borderColor: currentPage === page ? '#0B214E' : '#E6E5E5', background: currentPage === page ? '#0B214E' : '#FFFFFF', color: currentPage === page ? '#FFFFFF' : '#1D2338', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
        >
          {page}
        </button>
      ))}

      {pages[pages.length - 1] < totalPages && (
        <>
          {pages[pages.length - 1] < totalPages - 1 && <span style={{ fontSize: 13, color: '#9CA3AF' }}>...</span>}
          <button onClick={() => onPageChange(totalPages)} style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #E6E5E5', background: '#FFFFFF', color: '#1D2338', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>{totalPages}</button>
        </>
      )}

      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #E6E5E5', background: '#FFFFFF', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="#1D2338" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

PaginationControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

const BulkuploadCsvErrorPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const errors = location.state?.errors || [];
  const headerSummary = location.state?.headerSummary || null;

  const [currentPage, setCurrentPage] = useState(1);

  const allowedBankTypes = [
    "sort_code", "bsb_code", "institution_no", "bank_code",
    "branch_code", "aba", "clabe", "cnaps", "ifsc", "bic"
  ];

  const nonBankHeaders = Array.from(
    new Set(
      errors.flatMap(err =>
        Object.keys(err.row || {}).filter(
          key => !allowedBankTypes.includes(key)
        )
      )
    )
  );

  if (!nonBankHeaders.includes("Bank_Account_Name")) {
    nonBankHeaders.unshift("Bank_Account_Name");
  }

  const allHeaders = [...nonBankHeaders];

  const totalPages = Math.ceil(errors.length / PAGE_SIZE);
  const visibleErrors = errors.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const headerDisplayMap = {
    Bank_Account_Name: "Bank_Acc_Name",
    Bank_Account_Number: "Account_Number",
    Payment_Type: "Account_Type",
    Beneficiary_FirstName: "Beneficiary_Name",
    Bank_Code: "Code",
    Date_Of_Birth: "Date_Of_Birth",
  };

  const getDisplayHeader = (key) => headerDisplayMap[key] || key;

  const leftAlignedHeaders = ["Bank_Account_Name", "Beneficiary_FirstName"];

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '32px 40px',
        backgroundColor: '#ffffff',
        backgroundImage: 'linear-gradient(180deg,rgba(248,250,252,1) 0%,rgba(239,246,255,0.3) 50%,rgba(238,242,255,0.4) 100%)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        boxSizing: 'border-box',
      }}
    >
      {/* Page Title */}
      <div style={{ fontSize: 24, fontWeight: 700, color: '#394784', textAlign: 'center', marginBottom: 24 }}>
        CSV Uploaded File Errors
      </div>

      {/* Header Validation Summary (if present) */}
      {headerSummary && (
        <div style={{
          maxWidth: 960, margin: '0 auto 20px',
          padding: 16, borderRadius: 12,
          border: '1px solid #FECACA', background: '#FEF2F2',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#B91C1C', marginBottom: 8 }}>
            File Header Validation
          </div>
          {headerSummary.missing?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#DC2626', marginBottom: 4 }}>
                Missing Required Columns:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {headerSummary.missing.map((col, idx) => (
                  <li key={idx} style={{ fontSize: 13, color: '#B91C1C' }}>{col}</li>
                ))}
              </ul>
            </div>
          )}
          {headerSummary.extra?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#D97706', marginBottom: 4 }}>
                Extra Columns Found:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {headerSummary.extra.map((col, idx) => (
                  <li key={idx} style={{ fontSize: 13, color: '#B45309' }}>{col}</li>
                ))}
              </ul>
            </div>
          )}
          {headerSummary.empty?.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#DC2626', marginBottom: 4 }}>
                Empty Column Headers Found:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {headerSummary.empty.map((_, idx) => (
                  <li key={idx} style={{ fontSize: 13, color: '#B91C1C' }}>Column #{idx + 1} (blank)</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Main Card */}
      <div style={{
        borderRadius: 16,
        border: '1px solid #F2F2F2',
        background: '#FFFFFF',
        boxShadow: '0px 4px 16px rgba(0,0,0,0.06)',
        padding: 24,
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: 960,
        margin: '0 auto',
      }}>

        {/* Go Back button — top right */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 24px',
              background: '#D26521',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0px 4px 24px rgba(157,79,26,0.36)',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: 0.2,
            }}
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>

        {errors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 15, fontWeight: 500 }}>
            No errors found in the CSV file.
          </div>
        ) : (
          <>
            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, border: '1px solid #E6E5E5' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E6E5E5' }}>
                    <th style={thStyle('center')}>Line No</th>
                    {/* Error column first */}
                    <th style={{ ...thStyle('left'), color: '#DC2626', minWidth: 180 }}>
                      Error
                    </th>
                    {allHeaders.map(header => (
                      <th
                        key={header}
                        style={thStyle(leftAlignedHeaders.includes(header) ? 'left' : 'center')}
                      >
                        {getDisplayHeader(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleErrors.map((err, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: idx < visibleErrors.length - 1 ? '1px solid #F2F2F2' : 'none',
                        background: '#FFFFFF',
                      }}
                    >
                      <td style={tdStyle('center')}>
                        {err.lineNo ? String(err.lineNo).padStart(2, '0') : '-'}
                      </td>

                      {/* Error cell first */}
                      <td style={{
                        ...tdStyle('left'),
                        color: '#DC2626',
                        fontWeight: 500,
                        minWidth: 180,
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                      }}>
                        {err.messages}
                      </td>

                      {allHeaders.map(header => (
                        <td
                          key={header}
                          style={tdStyle(leftAlignedHeaders.includes(header) ? 'left' : 'center')}
                        >
                          {err.row?.[header] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Data Validation Failed Banner — outside the white card */}
      {errors.length > 0 && (
        <div style={{
          maxWidth: 960,
          margin: '16px auto 0',
          padding: '16px 20px',
          borderRadius: 12,
          border: '1px solid #FFA2A2',
          background: '#FFE2E2',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: '#FFE2E2', border: '1.5px solid #FFE2E2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: 1,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#E7000B" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="#E7000B" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#A90909', marginBottom: 4 }}>
              Data validation failed
            </div>
            <div style={{ fontSize: 16, fontWeight: 400, color: '#9F0712' }}>
              Found errors in your uploaded file. Please correct them and upload again.
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const thStyle = (align = 'center') => ({
  padding: '12px 16px',
  textAlign: align,
  fontSize: 13,
  fontWeight: 600,
  color: '#1D2338',
  whiteSpace: 'nowrap',
  letterSpacing: 0.1,
  border: '1px solid #E6E5E5',
});

const tdStyle = (align = 'center') => ({
  padding: '14px 16px',
  textAlign: align,
  fontSize: 13,
  fontWeight: 400,
  color: '#1D2338',
  verticalAlign: 'middle',
  border: '1px solid #E6E5E5',
});

export default BulkuploadCsvErrorPreview;